import { isLoggedIn } from "@shapr3d/frontend-lib";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { SyncRequestClient } from "../stores/requestClient";
import {
  FileDownloadSchema,
  ProjectID,
  ProjectRoles,
  RevisionID,
  SharingSpaceSchema,
  TeamID,
  ViewLinkID,
  WebExportID,
} from "../types";
import { UserID } from "./useAccount";
import { useTeam } from "./useTeams";

const ProjectSchema = z.object({
  projectId: ProjectID,
  name: z.preprocess((name) => name || "Untitled Project", z.string()),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date(),
  uploadedByUserId: UserID.optional(),
});

const ProjectRevisionSchema = z.object({
  revisionId: RevisionID,
  parentRevisionId: RevisionID.optional(),
  uploadedByUserId: UserID.optional(),
  uploadedByDeviceId: z.string(),
  uploadedByPlatform: z.enum(["ios", "macos", "visionos", "windows"]),
  createdAt: z.coerce.date(),
  uploadedAt: z.coerce.date().optional(),
  lastModifiedAt: z.coerce.date(),
  fileSize: z.number(),
  appVersion: z.string(),
  projectVersion: z.number(),
  copyOfRevisionId: RevisionID.optional(),
  fileDownload: FileDownloadSchema.optional(),
  thumbnailDownloads: z
    .object({
      light: FileDownloadSchema.optional(),
      dark: FileDownloadSchema.optional(),
    })
    .optional(),
});

const ProjectPageViewLinkSchema = z.object({
  projectViewLinkId: ViewLinkID,
  webExportId: WebExportID,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  published: z.boolean(),
  uploadedByUserId: UserID.optional(),
});
const UserSchema = z.object({
  userId: UserID,
  name: z.string().optional(),
  email: z.string().email(),
});

const ProjectDataSchema = z.object({
  project: ProjectSchema,
  latestRevision: ProjectRevisionSchema,
  projectViewLinks: z.array(ProjectPageViewLinkSchema).optional(),
  users: z.array(UserSchema).optional(),
  space: SharingSpaceSchema.pick({ spaceName: true, spaceType: true, spaceId: true })
    .partial()
    .optional(),
  teamId: TeamID,
  userRole: z.enum(ProjectRoles),
  isPrivateCloud: z.boolean().optional(),
});

export type ProjectData = z.infer<typeof ProjectDataSchema>;

export type Project = z.infer<typeof ProjectSchema>;

export const projectQueryKey = ["projectQueryKey"];

export const useProject = (isInviteAcceptancePending = false) => {
  const { projectId } = useParams();
  return useQuery({
    queryKey: [...projectQueryKey, projectId],
    queryFn: async () => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/projects/${projectId}`,
      });
      return ProjectDataSchema.parse(await response.json());
    },
    refetchInterval: 1000 * 60 * 60, // 1 hour
    enabled: isLoggedIn() && !isInviteAcceptancePending && Boolean(projectId),
  });
};

const ProjectsResponseSchema = z.object({
  projects: z.array(ProjectSchema),
  hasNextPage: z.boolean(),
  totalCount: z.number(),
  users: z.array(UserSchema).optional(),
});

export const useProjects = ({ teamID }: { teamID: string | undefined }) => {
  const { teamData } = useTeam(teamID);
  const isSyncOffTeam = teamData?.addons?.syncForbidden;

  return useQuery({
    queryKey: [...projectQueryKey, teamID],
    queryFn: async () => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/projects?teamId=${teamID}&onlySharedProjects=true&offset=0&maxPageSize=100`,
      });
      return ProjectsResponseSchema.parse(await response.json());
    },
    refetchInterval: 1000 * 60 * 60, // 1 hour
    enabled: isLoggedIn() && Boolean(teamID) && !isSyncOffTeam,
  });
};
